import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { CleaningCommandErrorAlert } from '../CleaningCommandErrorAlert/CleaningCommandErrorAlert';
import { useCurrentRouteData } from '../../hooks/useCurrentRouteData';
import { useDownloadRouteReport } from '../../hooks/useDownloadRouteReport';
import { useLocateToggles } from '../../hooks/useLocateToggles';
import { useRoutineTimeline } from '../../hooks/useRoutineTimeline';
import { PAUSE_RESUME_COMMAND_TYPES, SKIP_ROUTE_COMMAND_TYPES } from '../../utils/constants';
import type { CleaningControlAction } from '../../types/CleaningControls.types';
import {
  CloseButtonIcon,
  Body,
  Footer,
  Header,
  HeaderTitleContainer,
  LiveMapScreen,
  MachineName,
  ScheduleName,
  RightSide,
  RightSideLoading,
  LeftSide,
  ButtonGroupContainer,
  FooterButtonIcon,
} from './MachineDetailsRobotLiveMapScreen.styles';
import { ModalMessage, ModalType } from './components/ModalMessage';
import { LiveMapPanel } from './components/LiveMapPanel/LiveMapPanel';
import { RoutineTimeline } from './components/RoutineTimeline/RoutineTimeline';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { Styling } from 'app/cross-cutting-concerns/styling/Styling';
import { DependencyInjection } from 'app/cross-cutting-concerns/dependency-injection/DependencyInjection';
import { KCCThemeGlobalStyles } from 'config/theme/kccTheme/globalStyles';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { MapView } from 'app/modules/machine-inventory/machine-details-panel/components/MachineDetailsRobotPanel/LiveMap/components/MapView/MapView';
import { RobotCleaningControlActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSlice';
import { LiveMapActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/liveMapSlice';
import {
  selectLiveMapActiveCleaningZoneIds,
  selectLiveMapCleaningZoneFeatures,
  selectLiveMapCleaningZones,
  selectLiveMapImageUrl,
  selectLiveMapLivePosition,
  selectLiveMapMapInfo,
  selectLiveMapPlannedPath,
  selectLiveMapStatus,
  selectLiveMapSubscriptionStatus,
  selectLiveMapTrail,
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/liveMapSelectors';
import {
  getCurrentRouteStartTime,
  getTrailSince,
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/utils/routeTrail';
import {
  selectRoutineStoppedStep,
  selectRoutineTimeline,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/routineTimelineSelectors';
import { selectRobotCleaningProgressEntry } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/robotCleaningProgressSelectors';
import type { IState } from 'app/cross-cutting-concerns/state-management/interfaces/IState';
import type { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import type { RobotModelForLiveMap } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/liveMapActions.types';

interface IMachineDetailsRobotLiveMapScreenProps {
  isOpen: boolean;
  onClose: () => void;
  showSkipRoute?: boolean;
  onSkipRoute?: () => void;
  /** The current task is over: the cleaning controls have nothing left to act on. */
  hasTaskEnded?: boolean;
  isPaused: boolean;
  pendingAction: CleaningControlAction | null;
  isDisabled: boolean;
  /** Send home, Cancel cleaning and Skip route wait for Resume (B50 while paused). */
  isBlockedWhilePaused?: boolean;
  /** Opens the Send home confirmation modal. */
  onSendHome: () => void;
  onPauseOrResume: () => void;
  /** Opens the Cancel cleaning confirmation modal. */
  onCancelCleaning: () => void;
  model: RobotModelForLiveMap;
  robotName: string;
  scheduleName: string;
  mapName: string;
  deviceId: string;
  materialNumber: string;
  serialNumber: string;
  /** B50 only: repeat-path id. */
  routeId?: string;
  robot: Machine;
}

export const MachineDetailsRobotLiveMapScreen = ({
  isOpen,
  onClose,
  showSkipRoute = false,
  onSkipRoute,
  hasTaskEnded = false,
  isPaused,
  pendingAction,
  isDisabled,
  isBlockedWhilePaused = false,
  onSendHome,
  onPauseOrResume,
  onCancelCleaning,
  model,
  robotName,
  scheduleName,
  mapName,
  deviceId,
  materialNumber,
  serialNumber,
  routeId,
  robot,
}: IMachineDetailsRobotLiveMapScreenProps): JSX.Element => {
  const styling = DependencyInjection.resolve<Styling>(Styling);
  const kccAntdTheme = styling.getAntDesignKCCThemeConfig();
  const { t } = useTranslation();
  const currentRoute = useCurrentRouteData({
    robot,
    model,
    fallbackName: model === 'B50' ? scheduleName : mapName,
    isActive: isOpen,
  });
  // The routine timeline is B50 routine data. A single route run and CV50 whole-map cleaning have no right side.
  // TODO: CV50 AREA cleaning (activeCleaningZoneIds != null) will get its own right side; no UI yet.
  const routineTimeline = useRoutineTimeline({
    robot,
    isActive: isOpen && model === 'B50',
    currentRoute,
    scheduleName,
  });
  // The task's name lags behind its step on a route change, so the card takes the routine's name for the step.
  const routineRouteName = routineTimeline.view?.timeline.find(item => item.state === 'cleaning')?.name;
  const liveMapRoute = routineRouteName ? { ...currentRoute, name: routineRouteName } : currentRoute;
  // Nothing is left to skip once the last route is done or the run was stopped.
  const canSkipRoute = showSkipRoute && !currentRoute.isDone && !currentRoute.isStopped;
  const hasRoutineUi = routineTimeline.status === 'loading' || routineTimeline.status === 'ready';
  const { exportingReportId, downloadReport } = useDownloadRouteReport(robot);
  const dispatch = useDispatch();
  // CV50 Beep / Blink follow the device's reported settings (the paused-state subscription the controls open).
  const {
    isBeepOn,
    isBlinkOn,
    pendingCommand: pendingLocateCommand,
    isLocked: isLocateLocked,
    toggle: handleToggleLocateCommand,
  } = useLocateToggles({ materialNumber, serialNumber });

  const mapInfo = useSelector(selectLiveMapMapInfo);
  const mapImageUrl = useSelector(selectLiveMapImageUrl);
  // Memoized so the map, which only redraws when its props change, does not for every re-render of the screen.
  const mapImageDimensionsPx = useMemo(
    () => (mapInfo ? { width: mapInfo.imageWidthPx, height: mapInfo.imageHeightPx } : null),
    [mapInfo]
  );
  const plannedPath = useSelector(selectLiveMapPlannedPath);
  const trail = useSelector(selectLiveMapTrail);
  const livePosition = useSelector(selectLiveMapLivePosition);
  const subscriptionStatus = useSelector(selectLiveMapSubscriptionStatus);
  const liveMapStatus = useSelector(selectLiveMapStatus);
  // 'idle' covers the first render, before the open request has been dispatched.
  const isMapLoading = liveMapStatus === 'idle' || liveMapStatus === 'loading';
  const cleaningZones = useSelector(selectLiveMapCleaningZones);
  const cleaningZoneFeatures = useSelector(selectLiveMapCleaningZoneFeatures);
  const activeCleaningZoneIds = useSelector(selectLiveMapActiveCleaningZoneIds);

  // The map belongs to the route being cleaned, so it is fetched again when the route changes (e.g. after a skip).
  // The last known id is kept while the robot reports none between two routes.
  const lastRouteIdRef = useRef(routeId);
  if (routeId) {
    lastRouteIdRef.current = routeId;
  }

  const mapRouteId = lastRouteIdRef.current;

  const routineReports = useSelector(selectRoutineTimeline).reports;
  const task = useSelector((state: IState) => selectRobotCleaningProgressEntry(state, robot.id));
  // A stopped run's step moves on as the robot docks; its trail stays the one of the route it stopped on.
  const stoppedStep = useSelector((state: IState) => selectRoutineStoppedStep(state, task?.deviceTaskId));
  const trailStep = stoppedStep ?? task?.step;

  // The location history covers the whole run, so a routine run's trail is cut at the start of its current route.
  // Until the previous route's report gives that time, the moment the map moved on to the new route stands in.
  const [routeChangedAt, setRouteChangedAt] = useState<string | null>(null);
  // The step is part of the key: a routine can repeat a route id back to back.
  const routeKey = `${mapRouteId}:${trailStep}`;
  const shownRouteKeyRef = useRef(routeKey);

  useEffect(() => {
    if (!isOpen) {
      setRouteChangedAt(null);
    } else if (shownRouteKeyRef.current !== routeKey) {
      setRouteChangedAt(new Date().toISOString());
    }

    shownRouteKeyRef.current = routeKey;
  }, [isOpen, routeKey]);
  const routeTrail = useMemo(
    () =>
      hasRoutineUi ? getTrailSince(trail, getCurrentRouteStartTime(routineReports, trailStep, routeChangedAt)) : trail,
    [hasRoutineUi, trail, routineReports, trailStep, routeChangedAt]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    dispatch(RobotCleaningControlActions.dismissCleaningCommandError());
    dispatch(
      LiveMapActions.openLiveMapRequest({
        robotId: deviceId,
        model,
        deviceId,
        routeId: mapRouteId,
        materialNumber,
        serialNumber,
      })
    );

    return (): void => {
      dispatch(LiveMapActions.closeLiveMap());
      // A failed command's error badge is not carried over to the next time the map is opened.
      dispatch(RobotCleaningControlActions.dismissCleaningCommandError());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mapRouteId]);

  const [isSkipRouteModalOpen, setIsSkipRouteModalOpen] = useState(false);

  // The modal is rendered outside the live map screen, so closing the map must close it too.
  useEffect(() => {
    if (!isOpen) {
      setIsSkipRouteModalOpen(false);
    }
  }, [isOpen]);

  const openSkipRouteModal = (): void => {
    if (isDisabled || isBlockedWhilePaused) {
      return;
    }

    setIsSkipRouteModalOpen(true);
  };

  const closeSkipRouteModal = (): void => setIsSkipRouteModalOpen(false);

  const handleConfirmSkipRoute = (): void => {
    onSkipRoute?.();
    closeSkipRouteModal();
  };

  return (
    <ConfigProvider theme={kccAntdTheme} modal={{ className: 'kcc-theme-modal' }}>
      <KCCThemeGlobalStyles />
      <LiveMapScreen
        open={isOpen}
        keyboard={false}
        closable={false}
        onCancel={onClose}
        width="100%"
        title={
          <Header>
            <HeaderTitleContainer>
              <MachineName>{robotName}</MachineName>
              <ScheduleName>{scheduleName}</ScheduleName>
            </HeaderTitleContainer>
            <CloseButtonIcon onClick={onClose} aria-label={t('common.close')} />
          </Header>
        }
        footer={
          <Footer>
            <CleaningCommandErrorAlert commandTypes={[...PAUSE_RESUME_COMMAND_TYPES, ...SKIP_ROUTE_COMMAND_TYPES]} />

            {!hasTaskEnded && (
              <ButtonGroupContainer>
                <KccButton
                  type="secondary"
                  disabled={isDisabled || isBlockedWhilePaused}
                  icon={
                    <FooterButtonIcon>
                      {pendingAction === 'sendHome' ? <PendingSpinnerIcon /> : <SvgIcon name="house" size={20} />}
                    </FooterButtonIcon>
                  }
                  onClick={onSendHome}
                >
                  {t('robotCleaningControl.sendHome.label')}
                </KccButton>
                <KccButton
                  // Loading and disabled share the outlined look; only loading shows the spinner.
                  type={isDisabled ? 'secondary' : 'primary'}
                  disabled={isDisabled}
                  icon={
                    <FooterButtonIcon>
                      {pendingAction === 'pauseResume' ? (
                        <PendingSpinnerIcon />
                      ) : (
                        <SvgIcon name={isPaused ? 'playTight' : 'pauseTight'} size={20} />
                      )}
                    </FooterButtonIcon>
                  }
                  onClick={onPauseOrResume}
                >
                  {t(isPaused ? 'robotCleaningControl.resume.label' : 'robotCleaningControl.pause.label')}
                </KccButton>
                <KccButton
                  type="secondary"
                  disabled={isDisabled || isBlockedWhilePaused}
                  icon={
                    <FooterButtonIcon>
                      {pendingAction === 'cancelCleaning' ? (
                        <PendingSpinnerIcon />
                      ) : (
                        <SvgIcon name="cancel" size={20} />
                      )}
                    </FooterButtonIcon>
                  }
                  onClick={onCancelCleaning}
                >
                  {t('robotCleaningControl.cancelCleaning.label')}
                </KccButton>
              </ButtonGroupContainer>
            )}
          </Footer>
        }
      >
        <Body>
          <LeftSide>
            <LiveMapPanel
              currentRoute={liveMapRoute}
              isPaused={isPaused}
              isSingleRoute={model === 'CV50' || !hasRoutineUi}
              showSkip={model === 'B50' && hasRoutineUi && canSkipRoute}
              isSkipDisabled={isDisabled || isBlockedWhilePaused}
              isSkipPending={pendingAction === 'skipRoute'}
              onSkipRoute={openSkipRouteModal}
              showLocateActions={model === 'CV50'}
              isBeepOn={isBeepOn}
              isBlinkOn={isBlinkOn}
              isLocateDisabled={isLocateLocked}
              pendingLocateCommand={pendingLocateCommand}
              onToggleLocateCommand={handleToggleLocateCommand}
            >
              <MapView
                model={model}
                mapName={mapName}
                mapImageUrl={mapImageUrl}
                mapInfo={mapInfo}
                mapImageDimensionsPx={mapImageDimensionsPx}
                plannedPath={plannedPath}
                trail={routeTrail}
                livePosition={livePosition}
                subscriptionStatus={subscriptionStatus}
                cleaningZones={cleaningZones}
                cleaningZoneFeatures={cleaningZoneFeatures}
                activeCleaningZoneIds={activeCleaningZoneIds}
                isLoading={isMapLoading}
              />
            </LiveMapPanel>
          </LeftSide>
          {hasRoutineUi && (
            <RightSide>
              {routineTimeline.view ? (
                <RoutineTimeline
                  data={routineTimeline.view}
                  isPaused={isPaused}
                  showSkip={canSkipRoute}
                  isSkipDisabled={isDisabled || isBlockedWhilePaused}
                  isSkipPending={pendingAction === 'skipRoute'}
                  onSkipRoute={openSkipRouteModal}
                  onDownloadReport={item => item.reportId && downloadReport(item.reportId)}
                  exportingReportId={exportingReportId}
                />
              ) : (
                <RightSideLoading>
                  <Spin />
                </RightSideLoading>
              )}
            </RightSide>
          )}
        </Body>
      </LiveMapScreen>

      <ModalMessage
        isOpen={isSkipRouteModalOpen}
        isPending={false}
        onClose={closeSkipRouteModal}
        onConfirm={handleConfirmSkipRoute}
        type={ModalType.SKIP_ROUTE}
      />
    </ConfigProvider>
  );
};
